import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, CalendarDays, Sprout, ImageOff, Users, ArrowRight, Heart, Sparkles } from 'lucide-react';
import { navigate, safeFetch } from './App';
import { EntityFavoritesMenu } from './components/EntityFavoritesMenu';

/* ------------------------------------------------------------------------
 * Beautiful Rolodex
 *
 * Image-forward, vertical card grid. Each card behaves like a tiny,
 * tactile postcard: big hero image up top, generous padding below, a
 * gentle hover lift, and a glass-style favorites action that floats over
 * the image without ever fighting the card click.
 *
 * One responsive grid layout:
 *   sm    →   1 column
 *   md    →   2 columns
 *   xl    →   3 columns
 *
 * Pass-through props are unchanged from the previous Rolodex, so this is
 * a drop-in replacement.
 * --------------------------------------------------------------------- */

/* ---------------- Helpers ---------------- */

function getImageUrl(item) {
    return item?.image || item?.cover_image || item?.image_key || null;
}

function getTitle(item) {
    return item?.name || item?.business_name || item?.group_name || 'ללא שם';
}

function getDescription(item) {
    return item?.description || item?.business_description || '';
}

function getCategories(item, type) {
    let raw = [];
    if (type === 'business') {
        raw = [
            ...(Array.isArray(item?.business_categories) ? item.business_categories : []),
            ...(Array.isArray(item?.categories) ? item.categories : []),
        ];
    } else if (type === 'event') {
        raw = [
            ...(Array.isArray(item?.event_categories) ? item.event_categories : []),
            ...(Array.isArray(item?.business_categories) ? item.business_categories : []),
            ...(Array.isArray(item?.categories) ? item.categories : []),
        ];
    } else {
        raw = [...(Array.isArray(item?.categories) ? item.categories : [])];
    }
    if (!raw.length) {
        if (typeof item?.category === 'string' && item.category.trim()) {
            raw = item.category.split(',').map((s) => s.trim()).filter(Boolean);
        } else if (typeof item?.categories === 'string' && item.categories.trim()) {
            raw = item.categories.split(',').map((s) => s.trim()).filter(Boolean);
        }
    }
    return Array.from(new Set(raw.filter(Boolean))).map((c) => c.substring(0, 40));
}

function formatEventDate(rawDate) {
    if (!rawDate) return null;
    const date = new Date(rawDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('he-IL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function parseBarter(description) {
    if (!description) return { offer: '', want: '' };
    const offerMatch = description.match(/מה אני מציע:\s*(.+?)(?=\n\nמה אני מחפש:|$)/s);
    const wantMatch  = description.match(/מה אני מחפש:\s*(.+?)$/s);
    return {
        offer: offerMatch ? offerMatch[1].trim() : '',
        want:  wantMatch  ? wantMatch[1].trim()  : '',
    };
}

/* ---------------- Sub-components ---------------- */

function CardImage({ src, alt, onLoaded, loaded }) {
    const [errored, setErrored] = useState(false);

    if (!src || errored) {
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand-50 to-secondary-50">
                <div className="flex flex-col items-center gap-2 text-brand-400">
                    <Sprout className="w-10 h-10" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-brand-500/70">ללא תמונה</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {!loaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-surface-sunken via-surface-muted to-surface-sunken animate-pulse-soft" />
            )}
            <img
                src={src}
                alt={alt}
                loading="lazy"
                onLoad={onLoaded}
                onError={() => { setErrored(true); onLoaded(); }}
                className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-500 ease-smooth group-hover:scale-[1.04] ${loaded ? 'opacity-100' : 'opacity-0'}`}
            />
            {/* Soft bottom gradient for legibility of any overlaid text */}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        </>
    );
}

function CategoryPills({ categories, max = 2, type }) {
    if (!categories?.length) return null;
    const visible = categories.slice(0, max);
    const extra = categories.length - visible.length;

    // Soft, on-brand pill, never loud.
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {visible.map((c, i) => (
                <span
                    key={i}
                    className="inline-flex items-center text-2xs font-semibold uppercase tracking-wide text-brand-700 bg-brand-50 ring-1 ring-brand-200/50 rounded-pill px-2 py-0.5"
                >
                    {c}
                </span>
            ))}
            {extra > 0 && (
                <span className="text-2xs font-semibold text-ink-subtle">+{extra}</span>
            )}
        </div>
    );
}

function FounderAvatar({ src, name }) {
    if (!src) return null;
    return (
        <div className="absolute bottom-3 end-3 group/avatar">
            <img
                src={src}
                alt={name || ''}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="w-10 h-10 rounded-pill object-cover ring-2 ring-white shadow-md"
            />
            {name && (
                <span className="absolute bottom-full end-0 mb-2 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-200 pointer-events-none bg-ink/85 text-ink-inverse text-2xs font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-md">
                    {name}
                </span>
            )}
        </div>
    );
}

function BarterContent({ offer, want }) {
    return (
        <div className="grid grid-cols-1 gap-1.5 mt-1">
            {offer && (
                <div className="flex items-start gap-1.5 rounded-md bg-success-50/70 ring-1 ring-success-100 p-2">
                    <span className="shrink-0 text-sm leading-none mt-0.5" aria-hidden="true">💚</span>
                    <div className="min-w-0 flex-1">
                        <p className="text-2xs font-bold text-success-700 mb-0.5">מציע</p>
                        <p className="text-xs text-ink leading-snug line-clamp-1">{offer}</p>
                    </div>
                </div>
            )}
            {want && (
                <div className="flex items-start gap-1.5 rounded-md bg-info-50/70 ring-1 ring-info-100 p-2">
                    <span className="shrink-0 text-sm leading-none mt-0.5" aria-hidden="true">🔍</span>
                    <div className="min-w-0 flex-1">
                        <p className="text-2xs font-bold text-info-700 mb-0.5">מחפש</p>
                        <p className="text-xs text-ink leading-snug line-clamp-1">{want}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- Single card ---------------- */

function EntityCard({
    item, type, index,
    onClick, onDelete, fromView,
    favoritesLists, onToggleFavorite, onCreateFavoriteList,
    chatId, zone,
}) {
    const [imgLoaded, setImgLoaded] = useState(false);

    const title = getTitle(item);
    const imageUrl = getImageUrl(item);
    const description = getDescription(item);
    const categories = getCategories(item, type);
    const isBarter = type === 'barter' || item?.type === 'barter';
    const isEvent  = type === 'event' || item?.event_time || item?.datetime;
    const isGroup  = type === 'group';
    const eventDate = isEvent ? formatEventDate(item?.event_time || item?.datetime) : null;

    const entityId = isGroup ? item.group_id : (item.id || item.business_id);
    const favType = type === 'business' ? 'businesses'
                   : type === 'event'    ? 'events'
                   : type === 'barter'   ? 'barters'
                   : type === 'group'    ? 'groups'
                   : 'businesses';

    const showFavorites = onToggleFavorite && onCreateFavoriteList && type !== 'zone';
    const showRemove = fromView === 'favorites-list' && onDelete;

    const barter = isBarter ? parseBarter(description) : null;

    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(index * 0.025, 0.2), ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.99 }}
            onClick={onClick}
            className="group relative text-right block w-full bg-surface rounded-xl border border-line-subtle hover:border-brand-200 hover:shadow-card transition-[border-color,box-shadow] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
        >
            <div className="flex items-stretch gap-3 p-2.5">
                {/* Square thumbnail (with founder avatar OUTSIDE the clip box so it isn't truncated) */}
                <div className="relative shrink-0 w-20 sm:w-24">
                    <div className="aspect-square rounded-lg overflow-hidden bg-surface-muted">
                        <CardImage
                            src={imageUrl}
                            alt={title}
                            loaded={imgLoaded}
                            onLoaded={() => setImgLoaded(true)}
                        />
                    </div>
                    {item?.founder_image_url && !isGroup && (
                        <img
                            src={item.founder_image_url}
                            alt=""
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="absolute -bottom-1.5 -end-1.5 w-7 h-7 rounded-pill object-cover ring-2 ring-white shadow-md"
                        />
                    )}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 flex flex-col justify-between py-0.5">
                    <div className="min-w-0">
                        {/* Top row: category eyebrow OR event date OR group badge */}
                        <div className="flex items-center gap-1.5 mb-1 min-w-0">
                            {isEvent && eventDate ? (
                                <span className="inline-flex items-center gap-1 text-2xs font-bold text-brand-700 truncate">
                                    <CalendarDays className="w-3 h-3 shrink-0" strokeWidth={2.5} />
                                    <span className="truncate">{eventDate}</span>
                                </span>
                            ) : isGroup ? (
                                <span className="inline-flex items-center gap-1 text-2xs font-bold text-brand-700">
                                    <Users className="w-3 h-3" strokeWidth={2.5} />
                                    <span>קבוצה</span>
                                </span>
                            ) : categories.length > 0 ? (
                                <span className="text-2xs font-semibold uppercase tracking-wide text-brand-700 truncate">
                                    {categories.slice(0, 2).join(' · ')}
                                </span>
                            ) : null}
                        </div>

                        {/* Title */}
                        <h3 className="text-sm font-bold text-ink leading-snug line-clamp-2">
                            {title}
                        </h3>

                        {/* Description / barter content */}
                        {!isBarter && description && (
                            <p className="text-xs text-ink-muted leading-snug line-clamp-1 mt-0.5">
                                {description}
                            </p>
                        )}
                        {isBarter && barter && (barter.offer || barter.want) && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-ink-muted">
                                {barter.offer && (
                                    <span className="inline-flex items-center gap-1 min-w-0 truncate">
                                        <span aria-hidden="true">💚</span>
                                        <span className="truncate">{barter.offer}</span>
                                    </span>
                                )}
                                {barter.want && (
                                    <span className="inline-flex items-center gap-1 min-w-0 truncate">
                                        <span aria-hidden="true">🔍</span>
                                        <span className="truncate">{barter.want}</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Trailing actions — favorites / remove */}
                <div className="shrink-0 flex flex-col items-center justify-center gap-1 self-center">
                    {showFavorites && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <EntityFavoritesMenu
                                entityId={entityId}
                                entityType={favType}
                                chatId={chatId}
                                favoritesLists={favoritesLists}
                                onToggle={onToggleFavorite}
                                onCreateList={onCreateFavoriteList}
                                zone={zone}
                            />
                        </div>
                    )}
                    {showRemove && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDelete(entityId); }}
                            title="הסר מהרשימה"
                            aria-label="הסר מהרשימה"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-ink-faint hover:text-danger-600 hover:bg-danger-50 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </motion.button>
    );
}

/* ---------------- Zone card (different shape) ---------------- */

function ZoneCard({ item, index, onClick }) {
    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.32) }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.99 }}
            onClick={onClick}
            className="group relative w-full text-right bg-surface rounded-2xl overflow-hidden border border-line-subtle shadow-card hover:shadow-elevated transition-shadow ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
        >
            <div className="flex items-center gap-4 p-5">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 text-ink-inverse shadow-md ring-2 ring-white">
                    <MapPin size={26} strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                    <h3 className="heading-sm text-ink line-clamp-1">{item.business_name || item.zone_id}</h3>
                    {item.business_description && (
                        <p className="text-sm text-ink-muted line-clamp-1 mt-0.5">{item.business_description}</p>
                    )}
                </div>
                <ArrowRight className="w-5 h-5 text-ink-faint rotate-180 group-hover:text-brand-700 group-hover:-translate-x-1 transition-[color,transform]" strokeWidth={2.5} />
            </div>
        </motion.button>
    );
}

/* ---------------- Empty state ---------------- */

function EmptyRolodex({ type }) {
    const labelByType = {
        business: 'אין עדיין עסקים באזור',
        event:    'אין מפגשים מתוכננים',
        barter:   'אין בארטרים פעילים',
        group:    'אין עדיין קבוצות',
        zone:     'אין קהילות זמינות',
    };
    return (
        <div className="flex flex-col items-center justify-center text-center py-16 px-6 surface-soft rounded-2xl">
            <div className="relative mb-5">
                <span className="absolute inset-0 bg-brand-200/40 rounded-pill blur-2xl" aria-hidden="true" />
                <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-pill bg-surface ring-4 ring-brand-50">
                    <Sparkles className="w-9 h-9 text-brand-500" strokeWidth={1.75} />
                </span>
            </div>
            <h3 className="heading-sm text-ink mb-2">{labelByType[type] || 'אין פריטים להצגה'}</h3>
            <p className="text-sm text-ink-muted max-w-xs">ברגע שיתווסף משהו חדש כאן, הוא יופיע במקום הזה.</p>
        </div>
    );
}

/* ---------------- Detail panel (groups only — preserved logic) ---------------- */

function GroupDetailPanel({ selectedItem, itemDetails, isEditMode, editedDetails, isLoading, chatId, zone, onClose, onUpdate, onDelete, setIsEditMode, setEditedDetails }) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleSaveEdit = async () => {
        if (!editedDetails || !selectedItem || !zone) return;
        try {
            const response = await fetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-group',
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        group_id: selectedItem.group_id || selectedItem.id,
                        zone_id: zone,
                        name: editedDetails.name,
                        description: editedDetails.description,
                        chats: editedDetails.chat_links || [],
                        sites: editedDetails.website_links || [],
                    }),
                },
            );
            if (!response.ok) throw new Error('Failed');
            onUpdate(editedDetails);
            alert('הקבוצה עודכנה בהצלחה!');
        } catch {
            alert('שגיאה בעדכון הקבוצה');
        }
    };

    const handleDelete = async () => {
        if (!selectedItem || !zone) return;
        try {
            const response = await fetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-group',
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        group_id: selectedItem.group_id || selectedItem.id,
                        zone_id: zone,
                    }),
                },
            );
            if (!response.ok) throw new Error('Failed');
            alert('הקבוצה נמחקה בהצלחה!');
            onDelete(selectedItem.group_id || selectedItem.id);
            onClose();
        } catch {
            alert('שגיאה במחיקת הקבוצה');
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const isOwner = itemDetails.owner === chatId;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-modal-back"
            />
            <motion.aside
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="fixed top-0 end-0 h-full w-full max-w-md bg-surface shadow-2xl z-modal overflow-y-auto"
            >
                <div className="sticky top-0 bg-surface/95 backdrop-blur border-b border-line-subtle px-5 py-4 flex items-center justify-between z-10">
                    <h2 className="heading-sm text-ink">פרטי קבוצה</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-2 text-ink-muted hover:text-ink transition-colors px-3 py-1.5 rounded-md hover:bg-surface-muted"
                    >
                        <ArrowRight className="w-4 h-4" />
                        <span className="text-sm font-semibold">חזור</span>
                    </button>
                </div>

                <div className="p-5">
                    {isLoading ? (
                        <p className="text-center py-8 text-ink-muted">טוען...</p>
                    ) : (itemDetails.image || itemDetails.image_key) ? (
                        <img
                            src={itemDetails.image || itemDetails.image_key}
                            alt={itemDetails.name}
                            className="w-full h-48 object-cover rounded-xl mb-4"
                        />
                    ) : null}

                    {isEditMode ? (
                        <div className="space-y-4">
                            <input
                                type="text"
                                value={editedDetails.name || ''}
                                onChange={(e) => setEditedDetails({ ...editedDetails, name: e.target.value })}
                                placeholder="שם הקבוצה"
                                className="w-full p-3 border border-line rounded-lg bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                            />
                            <textarea
                                value={editedDetails.description || ''}
                                onChange={(e) => setEditedDetails({ ...editedDetails, description: e.target.value })}
                                placeholder="תיאור הקבוצה"
                                rows={5}
                                className="w-full p-3 border border-line rounded-lg bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 resize-y"
                            />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setIsEditMode(false)} className="flex-1 px-4 py-2.5 rounded-lg bg-surface-muted hover:bg-surface-sunken text-ink font-semibold transition-colors">ביטול</button>
                                <button type="button" onClick={handleSaveEdit} className="flex-1 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-ink-inverse font-semibold transition-colors">שמור</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h3 className="heading-md mb-2">{itemDetails.name}</h3>
                            {itemDetails.description && (
                                <p className="text-ink leading-relaxed whitespace-pre-wrap mb-5">{itemDetails.description}</p>
                            )}
                            {Array.isArray(itemDetails.chats) && itemDetails.chats.length > 0 && (
                                <div className="space-y-2 mb-5">
                                    {itemDetails.chats.map((chat, idx) => (
                                        <a
                                            key={idx}
                                            href={chat.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-between p-3 bg-info-50 hover:bg-info-100 rounded-lg ring-1 ring-info-100 transition-colors"
                                        >
                                            <span className="text-sm font-medium text-info-700">🔗</span>
                                            <span className="text-sm text-ink font-medium">{chat.name}</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                            {isOwner && (
                                <div className="space-y-2 pt-4 border-t border-line-subtle">
                                    <button type="button" onClick={() => setIsEditMode(true)} className="w-full px-4 py-2.5 rounded-lg bg-info-500 hover:bg-info-600 text-ink-inverse font-semibold transition-colors">ערוך קבוצה</button>
                                    <button type="button" onClick={() => setShowDeleteConfirm(true)} className="w-full px-4 py-2.5 rounded-lg bg-danger-500 hover:bg-danger-600 text-ink-inverse font-semibold transition-colors">מחק קבוצה</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </motion.aside>

            {showDeleteConfirm && (
                <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-ink/50" onClick={() => setShowDeleteConfirm(false)} />
                    <div className="relative bg-surface rounded-xl p-6 max-w-sm w-full shadow-elevated">
                        <h3 className="heading-sm mb-4 text-right">למחוק את הקבוצה?</h3>
                        <div className="flex gap-2">
                            <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 rounded-lg bg-surface-muted hover:bg-surface-sunken text-ink font-semibold transition-colors">ביטול</button>
                            <button onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-lg bg-danger-500 hover:bg-danger-600 text-ink-inverse font-semibold transition-colors">מחק</button>
                        </div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
}

/* ---------------- Main Rolodex ---------------- */

const Rolodex = ({
    items = [],
    type = 'business',
    chatId,
    fromView,
    zone,
    onDelete,
    onItemClick,
    title,
    favoritesLists = [],
    onToggleFavorite = null,
    onCreateFavoriteList = null,
}) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemDetails, setItemDetails] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedDetails, setEditedDetails] = useState(null);

    const handleItemClick = (item) => {
        if (onItemClick) { onItemClick(item); return; }
        if (type === 'zone' || item.type === 'zone') {
            navigate('zone-details', `zone=${encodeURIComponent(item.zone_id)}`);
        } else if (type === 'group') {
            const id = item.group_id || item.id;
            if (!id) return;
            const zoneId = zone || item.zone_id || item.zone;
            if (zoneId) {
                setSelectedItem(item);
                setItemDetails(item);
                setEditedDetails(item);
                navigate('group', `group_id=${id}&zone=${encodeURIComponent(zoneId)}`);
            } else {
                navigate('group', `group_id=${id}`);
            }
        } else {
            const url = new URL(window.location.href);
            url.search = '';
            url.searchParams.set('entity', item.id || item.business_id);
            window.location.href = url.toString();
        }
    };

    if (!items || items.length === 0) {
        return <EmptyRolodex type={type} />;
    }

    return (
        <>
            <section className="relative">
                {title && (
                    <h3 className="heading-sm text-ink mb-4 px-1">{title}</h3>
                )}

                <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
                    {items.map((item, index) => {
                        const isZone = type === 'zone' || item.type === 'zone';
                        const key = isZone
                            ? (item.zone_id || index)
                            : (item.id || item.business_id || item.group_id || index);

                        return isZone ? (
                            <ZoneCard key={key} item={item} index={index} onClick={() => handleItemClick(item)} />
                        ) : (
                            <EntityCard
                                key={key}
                                item={item}
                                type={type}
                                index={index}
                                onClick={() => handleItemClick(item)}
                                onDelete={onDelete}
                                fromView={fromView}
                                favoritesLists={favoritesLists}
                                onToggleFavorite={onToggleFavorite}
                                onCreateFavoriteList={onCreateFavoriteList}
                                chatId={chatId}
                                zone={zone}
                            />
                        );
                    })}
                </div>
            </section>

            {/* Detail panel for groups (preserved from old implementation) */}
            {selectedItem && itemDetails && type === 'group' && (
                <GroupDetailPanel
                    selectedItem={selectedItem}
                    itemDetails={itemDetails}
                    isEditMode={isEditMode}
                    editedDetails={editedDetails}
                    isLoading={false}
                    chatId={chatId}
                    zone={zone}
                    onClose={() => { setSelectedItem(null); setItemDetails(null); setIsEditMode(false); setEditedDetails(null); }}
                    onUpdate={(updated) => { setItemDetails(updated); setIsEditMode(false); }}
                    onDelete={onDelete}
                    setIsEditMode={setIsEditMode}
                    setEditedDetails={setEditedDetails}
                />
            )}
        </>
    );
};

export default Rolodex;
